<template>
  <div class="signup-page">
    <div class="signup-page__left">
      <div class="signup-page__title">Join Our<br />Virtual Network</div>
      <div class="signup-page__desc">
        Not registered for our virtual network? Sign up here! Complete the form
        below and upload a photo to join.
      </div>
      <v-form lazy-validation ref="form" @submit.prevent="submit">
        <div class="d-flex signup-page__uploader-row">
          <v-btn class="btn-upload" color="red" @click="openFileDialog">
            Upload Photo
          </v-btn>
          <div class="align-self-start mx-1">*</div>
          <div class="align-self-end" v-if="form.source">
            {{ form.source.name }}
            <a href="#" @click="openFileDialog">Replace</a>
          </div>
        </div>

        <v-row>
          <v-col sm="6" cols="12" v-for="field in fields" :key="field.name">
            <v-select
              v-if="field.select"
              :items="field.options"
              item-value="id"
              :item-text="s => `${s.name}`"
              :label="field.label"
              v-model="form[field.name]"
              :rules="[validation.required]"
              validate-on-blur
            ></v-select>
            <v-text-field
              v-else-if="field.mask"
              :label="field.label"
              v-model="form[field.name]"
              :rules="[validation.required]"
              validate-on-blur
              color="#000"
              v-mask="getMask(field)"
            ></v-text-field>
            <v-text-field
              v-else
              :label="field.label"
              v-model="form[field.name]"
              :rules="field.required ? [validation.required] : false"
              validate-on-blur
              color="#000"
            ></v-text-field>
            <!-- v-mask="field ? field.mask || undefined : undefined" -->
          </v-col>
        </v-row>

        <div class="checkbox-with-link">
          <v-checkbox v-model="agree"> </v-checkbox>
          <div class="checkbox-with-link__label">
            By submitting your information, you are agreeing to the possibility
            that your name, likeness, and words may be publicly posted on this
            site and/or any other official St. Jude Children’s Research Hospital
            social media or digital platforms. View our
            <a
              href="https://drive.google.com/file/d/1JGxF39z6RryyUnMjM3BiO15qU-pFHISJ/view"
              target="_blank"
              >photo release terms</a
            >.
          </div>
        </div>

        <v-btn
          :disabled="uploading"
          class="btn-submit"
          color="red"
          type="submit"
          >{{ uploading ? `Uploading... ${progress}` : "Submit" }}</v-btn
        >
      </v-form>
    </div>
    <div class="signup-page__right">
      <div class="signup-page__img" />
    </div>

    <v-dialog
      persistent
      max-width="1280"
      v-if="fileDialogVisible"
      v-model="fileDialogVisible"
    >
      <v-card>
        <FileUploadForm
          @close="fileDialogVisible = false"
          @upload="changeFile"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formMixin from "@/mixins/form.mixin";
import FileUploadForm from "@/components/SignUp/FileUploadForm";

export default {
  name: "SignupPage",
  extends: formMixin,
  components: {
    FileUploadForm
  },
  metaInfo: {
    titleTemplate: "%s - SignUp"
  },
  data() {
    return {
      form: {},
      agree: false,
      progress: 0,
      uploading: false,
      submitted: false,
      fields: [
        {
          label: "First Name*",
          name: "first_name",
          required: true
        },
        {
          label: "Last Name*",
          name: "last_name",
          required: true
        },
        {
          label: "Position*",
          name: "job_position",
          required: true
        },
        {
          label: "Phone Number*",
          name: "phone",
          mask: "###-###-####",
          required: true
        },
        {
          label: "Email Address*",
          name: "email",
          required: true
        },
        {
          label: "LinkedIn Profile URL",
          name: "linkedin",
          required: false
        }
      ],
      fileDialogVisible: false
    };
  },
  methods: {
    async submit() {
      if (!this.validate() || !this.form.source) {
        return this.$alert(
          "Form is invalid, please fill all required fields",
          "error"
        );
      }
      if (!this.agree) {
        return this.$alert(
          "You must agree to the processing of personal data",
          "error"
        );
      }

      try {
        const formData = new FormData();
        this.uploading = true;

        for (const key in this.form) {
          formData.append(`contact[${key}]`, this.form[key]);
        }

        await this.$recaptchaLoaded();
        const recaptcha = await this.$recaptcha("login");
        formData.append("recaptcha", recaptcha);

        await this.$api.Contact.create(formData, {
          onUploadProgress: progress => {
            this.progress = progress;
          }
        });
        this.uploading = false;
        this.progress = 0;
        this.submitted = true;

        this.$router.push("/sign-up-thanks");
      } catch {
        this.$alert("Something went wrong", "error");
        this.uploading = false;
        this.progress = 0;
      }
    },
    changeFile(fileSource) {
      this.form.source = fileSource;
    },
    openFileDialog() {
      this.fileDialogVisible = true;
    },
    getMask(field) {
      return field && field.mask ? field.mask : undefined;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.signup-page {
  display: flex;
  background-color: #f5f5f5;
  color: $primary;

  .btn-upload,
  .btn-submit {
    color: white;
  }

  &__left {
    flex: 1;
    padding: 80px 50px;
  }

  &__right {
    flex: 1;
  }

  &__img {
    width: 100%;
    height: 100%;
    background-image: url(~@/assets/signup-bg.jpg);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }

  &__title {
    font-weight: 800;
    font-size: 64px;
    line-height: 1.14;
  }

  &__desc {
    margin: 39px 0 60px;
    font-size: 18px;
    line-height: 24px;
    color: #000000;
  }

  &__uploader-row {
    font-weight: 800;
    font-size: 13px;
    line-height: 17px;
    margin-bottom: 20px;
  }

  a {
    color: #1874dc;
  }
}

.checkbox-with-link {
  display: flex;
  margin-bottom: 1em;
  &__label {
    font-size: 14px;
  }
}

@media screen and (max-width: 960px) {
  .signup-page {
    flex-direction: column;
    &__title {
      font-size: 43px;
      line-height: 44px;
      text-align: center;
    }
    &__desc {
      text-align: center;
    }
    &__left {
      padding: 50px 40px;
    }
    &::v-deep {
      .v-btn:not(.v-btn--round).v-size--default {
        &.btn-upload,
        &.btn-submit {
          font-size: 16px;
          height: 36px;
          padding: 0 22px;
        }
      }
    }
  }
}
</style>
